<template>
  <div
    id="service-account"
    class="page-content"
  >
    <div class="card create-update-service-account">
      <div class="card-header">
        <h3 class="card-title">
          Service Account Information
        </h3>
      </div>
      <hr class="card-title-line">
      <div class="card-content">
        <form
          :id="pageId"
          method="post"
          class="form-horizontal"
          autocomplete="off"
          :class="pageId"
          @submit.prevent="submitForm"
        >
          <fieldset>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Service*</label>
              <div class="col-sm-3">
                <dropdown-list
                  v-model="serviceAccountInfo.service"
                  filterable
                  :options="gourmetServiceList"
                />
              </div>
              <label class="col-sm-2 control-label">User*</label>
              <div class="col-sm-3">
                <sanitized-input
                  id="user"
                  v-model="serviceAccountInfo.user"
                  required
                  placeholder="User"
                />
              </div>
            </div>
            <div class="row">
              <label class="col-sm-3 control-label pt-10">Email*</label>
              <div class="col-sm-3">
                <email-input
                  v-model="serviceAccountInfo.email"
                  required
                  :disabled="!!serviceAccountId"
                  @update-validation="updateEmailValidation"
                />
              </div>
              <label class="col-sm-2 control-label">Password*</label>
              <div class="col-sm-3">
                <sanitized-input
                  id="password"
                  v-model="serviceAccountInfo.password"
                  required
                  placeholder="Password"
                  autocomplete="new-password"
                />
              </div>
            </div>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Phone Number*</label>
              <phone-number-input
                id="phone-number"
                v-model="serviceAccountInfo.phoneNumber"
                class="col-sm-3"
                :page-id="pageId"
                :required="false"
              />
              <div class="col-sm-4 offset-2">
                <el-checkbox
                  id="isActive"
                  v-model="serviceAccountInfo.isActive"
                >
                  Active Account
                </el-checkbox>
              </div>
            </div>
            <div class="row align-items-center">
              <div class="col-sm-4 offset-3">
                <el-checkbox
                  id="isActive"
                  v-model="serviceAccountInfo.isAutomatedAccount"
                >
                  DB Ordering Account
                </el-checkbox>
              </div>
              <div class="col-sm-4 offset-1">
                <el-checkbox
                  id="isActive"
                  v-model="serviceAccountInfo.isManualAccount"
                >
                  Manual Account
                </el-checkbox>
              </div>
            </div>
            <div class="row align-items-center">
              <label class="col-sm-3 control-label">Assigned Operator</label>
              <div class="col-sm-7">
                <dropdown-list
                  v-model="operatorIdModel"
                  filterable
                  clearable
                  :disabled="!serviceAccountInfo.isManualAccount"
                  :options="operatorOptions"
                />
                <small
                  v-if="!serviceAccountInfo.isManualAccount"
                  class="assigned-operator-hint"
                >
                  Operator can be assigned only to manual account
                </small>
                <el-alert
                  v-if="!!backupServiceAccount?.operator?.id"
                  class="operator-change-warning"
                  type="warning"
                  :closable="false"
                  show-icon
                >
                  <template #default>
                    Before changing the operator, confirm they are <strong>not currently processing an order</strong> to avoid disrupting an active workflow.
                  </template>
                </el-alert>
              </div>
            </div>
          </fieldset>
          <hr>
          <div class="form-submit-button-group">
            <p-button
              type="purple"
              round
              native-type="submit"
              :disabled="!isPhoneNumberValid || !isEmailValid"
            >
              {{ actionBtnText }}
            </p-button>

            <p-button
              type="yellow"
              round
              @click="resetServiceAccountForm"
            >
              Reset
            </p-button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, Ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useGlobal } from '@/use/global';
import { useSwalDialog } from '@/use/swalDialog';
import { gourmetServiceList } from '@/constants/deliveryOrder';
import { ListType } from '@/models/app';
import { ServiceAccount, ServiceAccountInput } from '@/models/serviceAccount';
import { serviceAccountService } from '@/services/serviceAccount';
import { fetchOperators } from '@/services/operators/fetchOperators';

const pageId = 'create-update-service-account';
const paginationLimit = 100;

export default {
  name: 'CreateUpdateServiceAccount',

  setup: () => {
    const router = useRouter();

    const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
    const { serviceAccountId } = useGlobal();

    const serviceAccountInfo: Ref<ServiceAccountInput> = ref({
      user: '',
      service: '',
      email: '',
      password: '',
      phoneNumber: '',
      isActive: true,
      isAutomatedAccount: false,
      isManualAccount: false,
    });
    const backupServiceAccount: Ref<ServiceAccount | null> = ref(null);
    const isEmailValid = ref(true);

    const operatorOptions: Ref<ListType[]> = ref([]);
    const operatorId: Ref<string | null> = ref(null);
    const operatorIdModel = computed<string>({
      get: () => operatorId.value ?? '',
      set: (value: string) => {
        operatorId.value = value ? value : null;
      },
    });

    const isPhoneNumberValid = computed(() => {
      return util.isPhoneNumberValid(serviceAccountInfo.value.phoneNumber, false);
    });

    const actionBtnText = computed(() => {
      return backupServiceAccount.value?.id ? 'Update' : 'Create';
    });

    const updateEmailValidation = (value: boolean) => {
      isEmailValid.value = value;
    };

    const loadAllGourmetOperators = async () => {
      await loadOperators();

      operatorOptions.value = [
        { id: 0, key: '', label: 'N / A' },
        ...operatorOptions.value,
      ];
    };

    const loadOperators = async (currentPage: number = 1) => {
      const offset = (currentPage - 1) * paginationLimit;

      try {
        const response = await fetchOperators({
          roles: ['gourmet-operator'],
          isActive: true,
          pagination: {
            offset,
            limit: paginationLimit,
          },
        });

        for (const operator of response.rows) {
          operatorOptions.value.push({
            id: operator.id,
            key: operator.id,
            label: util.getFullName(operator.firstName ?? '', operator.lastName ?? ''),
          });
        }

        if (operatorOptions.value.length < response.count) {
          return loadOperators(currentPage + 1);
        }
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
    };

    const getServiceAccountInfo = async () => {
      const loading = ElLoading.service({
        lock: true,
        text: 'Loading Service Account Info...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      try {
        backupServiceAccount.value = await serviceAccountService.fetchServiceAccount(serviceAccountId.value);

        serviceAccountInfo.value = {
          user: backupServiceAccount.value?.user ?? '',
          service: backupServiceAccount.value?.service ?? '',
          email: backupServiceAccount.value?.email ?? '',
          password: backupServiceAccount.value?.password ?? '',
          phoneNumber: backupServiceAccount.value?.phoneNumber ?? '',
          isActive: backupServiceAccount.value?.isActive ?? false,
          isAutomatedAccount: backupServiceAccount.value?.isAutomatedAccount ?? false,
          isManualAccount: backupServiceAccount.value?.isManualAccount,
        };
        operatorId.value = backupServiceAccount.value?.operator?.id ?? null;
      } catch (error) {
        console.log(util.getGraphqlError(error));
      }

      loading.close();
    };

    const createServiceAccountInfo = async (postData: ServiceAccountInput) => {
      try {
        await serviceAccountService.createServiceAccount(postData);

        showSuccessPopupDialog({
          text: 'The service account has been created successfully.',
        });

        router.push({
          name: 'ServiceAccounts',
          hash: '#service-accounts-table',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
    };

    const updateServiceAccountInfo = async (postData: ServiceAccountInput) => {
      try {
        await serviceAccountService.updateServiceAccount({
          id: backupServiceAccount.value!.id!,
          data: postData,
        });

        showSuccessPopupDialog({
          text: 'The service account has been updated successfully.',
        });

        router.push({
          name: 'ServiceAccounts',
          hash: '#all-service-accounts',
        });
      } catch (error) {
        const errorText = util.getGraphqlError(error, false);
        showErrorPopupDialog({
          text: errorText,
        });
      }
    };

    const submitForm = async () => {
      const phoneNumber = util.isPhoneNumberValid(serviceAccountInfo.value.phoneNumber) ? serviceAccountInfo.value.phoneNumber : '';

      const postData = {
        ...serviceAccountInfo.value,
        phoneNumber,
        operatorId: serviceAccountInfo.value.isManualAccount ? operatorId.value : null,
      } as ServiceAccountInput & { operatorId: string | null };

      const loading = ElLoading.service({
        lock: true,
        text: backupServiceAccount.value?.id ? 'Updating Service Account...' : 'Creating Service Account...',
        background: 'rgba(0, 0, 0, 0.7)',
        customClass: 'custom-full-spinner-class',
      });

      if (backupServiceAccount.value?.id) {
        // Update Service Account
        await updateServiceAccountInfo(postData);
      } else {
        // Create Service Account
        await createServiceAccountInfo(postData);
      }

      loading.close();
    };

    const resetServiceAccountForm = () => {
      if (backupServiceAccount.value) {
        serviceAccountInfo.value = {
          user: backupServiceAccount.value?.user,
          service: backupServiceAccount.value?.service,
          email: backupServiceAccount.value?.email,
          password: backupServiceAccount.value?.password,
          phoneNumber: backupServiceAccount.value?.phoneNumber,
          isActive: backupServiceAccount.value?.isActive,
          isAutomatedAccount: backupServiceAccount.value?.isAutomatedAccount,
          isManualAccount: backupServiceAccount.value?.isManualAccount,
        };
      } else {
        serviceAccountInfo.value = {
          user: '',
          service: '',
          email: '',
          password: '',
          phoneNumber: '',
          isActive: true,
          isAutomatedAccount: false,
          isManualAccount: false,
        };
      }
      operatorId.value = backupServiceAccount.value?.operator?.id ?? null;
    };

    watch(
      () => serviceAccountInfo.value.isAutomatedAccount,
      (value: boolean) => {
        if (value) {
          serviceAccountInfo.value.isManualAccount = false;
        }
      },
    );

    watch(
      () => serviceAccountInfo.value.isManualAccount,
      (value?: boolean | null) => {
        if (value) {
          serviceAccountInfo.value.isAutomatedAccount = false;
        } else {
          operatorId.value = null;
        }
      },
    );

    onMounted(() => {
      loadAllGourmetOperators();
      if (serviceAccountId.value) {
        getServiceAccountInfo();
      }
    });

    return {
      pageId,
      serviceAccountInfo,
      serviceAccountId,
      backupServiceAccount,
      isPhoneNumberValid,
      actionBtnText,
      gourmetServiceList,
      isEmailValid,
      operatorOptions,
      operatorId,
      operatorIdModel,

      submitForm,
      resetServiceAccountForm,
      updateEmailValidation,
    };
  },
};
</script>

<style scoped lang="scss">
.assigned-operator-hint {
  display: block;
  margin-top: 4px;
  color: #999;
  font-style: italic;
}

.operator-change-warning {
  margin-top: 8px;
}
</style>
